<template>
  <div class="setting-view common-view content-view">
    <common-header/>

    <div class="container" v-once>
      <h1 class="main-title">Press Kit</h1>

      <div class="block">
        <h2 class="title">Logo</h2>

        <div class="items">
          <press-item v-for="(item, index) in logos" :key="index" :model="item"/>
        </div>
      </div>

      <div class="block">
        <h2 class="title">Color Palette Generator</h2>

        <div class="items">
          <press-item v-for="(item, index) in colors" :key="index" :model="item"/>
        </div>
      </div>

      <div class="block">
        <h2 class="title">Color Theme Generator</h2>

        <div class="items">
          <press-item v-for="(item, index) in samples" :key="index" :model="item"/>
        </div>
      </div>

      <div class="block">
        <h2 class="title">Press Contacts for Colorful</h2>

        <div class="description">
          Terry Shapiro<br>
          <a href="mailto:press@colorful.co" class="link-inverse">press@colorful.co</a>
        </div>
      </div>

      <div class="block">
        <h2 class="title">About Colorful</h2>

        <div class="description">
          Colorful is a patented color theme generator that closes the creative gap between designers and their clients. Give it a color palette and Colorful produces a range of creative visual directions instantly, saving designers energy and time. While great use of color is essential to a great design, finding the right color palette doesn't help designers know how to use color in their designs. After choosing the perfect color palette with Colorful's built-in color palette generator or a 3rd party color palette generator, the system will calculate all the ways a designer could use the colors of that palette. The system will the produce hundreds of creative and unique options to browse from and preview uses of color in real-time. Once you find the perfect color theme, select and download a fully-baked design file (Starter Kit) colored to match your theme.<br>
          <br>
          Colorful segments its offerings into three areas; a color palette generator, a color theme generator, and a dynamic asset library. There are two two tiers in the pricing model, free accounts and paid memberships. The free account gives users access to creating and saving color palettes and color themes. Free accounts also offer 4 free starter kit downloads to users. A premium account offers designers unlimited access to color palettes, color themes and the starter kit asset library. Team pricing is also available for teams two or more. Additionally, Colorful offers a partner API allowing deep, custom integrations for a wide range of use-cases.<br>
          <br>
          Colorful's color palette generator, like most palette generators, presents beautiful color combinations with minimal effort (pressing the spacebar). Unlike any other color palette tool, however, Colorful's color palette generator allows designers to decide how many colors they'd like in their final palette - anywhere from 2 to 5 colors. Not all designers want or need 5 colors and may only want 3 colors in their palette. Colorful empowers designers to make this happen by simply toggling the number of values desired in the filter toolbar. Aside from generating attractive palettes, users can create a free account to save all the color palettes they'd like.<br>
          <br>
          Now that designers have the perfect set of colors, what are the options for how the colors could be used in a design? 'Should the background be green? Or should it be white with hints of green, no blue? Maybe a gradient between the green and blue on top of the white? What would a green-to-blue gradient look like over dark?' Too often designers are limited to their own 'styles' and are disincentivized to explore deeply how to uniquely use colors in design outputs because of its time-consuming nature. The Colorful team designed, developed and patented a process that computes virtually all possible ways to use colors in a design output using advanced color mapping AI logic and carefully-tooled color combination models. Feed it any any number and type of colors and it will produce hundreds of possible ways to use those colors beautifully in a design - i.e. 'Color themes.' When designers generate possible color combinations using Colorful for a set of colors, the interface makes it easy to narrow down options by style type and colors used. If you know the client wants a lighter-style UI with the brand blue, simply filter the vast number of results and find the perfect set to present.<br>
          <br>
          Once a color theme is selected, now is the time to see your theme come to life in a design or code file. Alongside the latest standards in design system architecture, Colorful's theme logic accounts for hundreds of objects within dozens of standarized UI components, making it simple to map and auto-color the most complex files. Today the system supports recoloring Sketch and InVision Studio design files. We plan to support a number of file types and coding frameworks in the near future. Using this technology, Colorful is laying the foundation to create and execute on the first dynamic design asset marketplace. Designer styles and client tastes all differ greatly, so shouldn't the assets they purchase to execute these projects be unique as well?<br>
          <br>
          Please direct all press-related questions to <a href="mailto:press@colorful.co" class="link-inverse">press@colorful.co</a>.
        </div>
      </div>
    </div>

    <app-footer/>
  </div>
</template>

<script>
import CommonHeader from './CommonHeader';
import AppFooter from './AppFooter';
import PressItem from '../components/press/PressItem';

export default {
  components: {
    PressItem,
    CommonHeader,
    AppFooter,
  },
  data() {
    return {
      logos: [
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/colorful-logo-1.jpg',
          name: 'Logo, Standard',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/logo/colorful_standard-logo.zip',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/colorful-logo-2.jpg',
          name: 'Logo, On Dark',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/logo/colorful_standard-logo-reversed.zip',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/colorful-logo-3.jpg',
          name: 'Logo, Watermark',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/logo/colorful_watermark-logo-white.zip',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/colorful-logo-4.jpg',
          name: 'Logo, Watermark',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/logo/colorful_watermark-logo-dark.zip',
        },
      ],
      colors: [
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/color-palette-1.jpg',
          name: 'Designer + Palettes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/palette/colorful-color-palette-generator_1.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/color-palette-2.jpg',
          name: 'Developer + Palettes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/palette/colorful-color-palette-generator_2.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/color-palette-3.jpg',
          name: 'Browse Palettes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/palette/colorful-color-palette-generator_3.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/color-palette-4.jpg',
          name: 'Saved Color Palettes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/palette/colorful-color-palette-generator_4.jpg',
        },
      ],
      samples: [
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/theme-generator-1.jpg',
          name: 'Mobile Color Themes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/sample/colorful-color-theme-1.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/theme-generator-2.jpg',
          name: 'Design System Themes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/sample/colorful-color-theme-2.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/theme-generator-3.jpg',
          name: 'Website Color Themes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/sample/colorful-color-theme-3.jpg',
        },
        {
          image: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/theme-generator-4.jpg',
          name: 'Mobile Color Themes',
          download: 'https://colorful-assets.s3-us-west-2.amazonaws.com/colorful-frontend/press/downloads/sample/colorful-color-theme-4.jpg',
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
  .big-title {
    margin-bottom: 80px;

    @include media($mobile) {
      margin-bottom: 40px;
    }
  }

  .title {
    font-size: 22px;
    margin-bottom: 25px;
  }

  .block {
    margin-bottom: 50px;

    @include media($mobile) {
      margin-bottom: 30px;
    }
  }

  .items {
    margin: 0 -10px;
    display: flex;
    flex-flow: row wrap;
  }

  .app-footer {
    padding: 30px 0;
  }
</style>
